import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  Button,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import {
  MdPendingActions,
  MdCheck,
  MdClose,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { updateLeaveAction } from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";

const PendingApprovalsWidget = ({
  userRole,
  leaves = [],
  requisitions = [],
  loading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";
  const { hasPermission } = usePermissions();
  const currentUser = useSelector(selectCurrentUser);

  const isEmployeeRole = userRole?.toLowerCase() === "employee";
  const isCompanyRole = userRole?.toLowerCase() === "company";

  // Permission checks
  const canApproveLeave =
    hasPermission("leave_details.approve") ||
    hasPermission("leave_details.view") ||
    hasPermission("leave_details.edit") ||
    hasPermission("leave_details.action") ||
    isCompanyRole;

  const canApplyLeave =
    hasPermission("apply_leave.view") ||
    hasPermission("apply_leave.create") ||
    isEmployeeRole;

  const showLeaves = canApproveLeave || canApplyLeave;
  const showRequisitions =
    hasPermission("requisitions.view") ||
    hasPermission("requisition.view") ||
    hasPermission("requisition.approve") ||
    !isEmployeeRole;

  const initialTab = showLeaves ? 0 : 1;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Filter leaves
  const pendingLeaves = (Array.isArray(leaves) ? leaves : []).filter((l) => {
    if (!l) return false;
    const s = String(l.status || l.approval_status || "").toLowerCase();
    return !s || s === "pending" || s === "submitted" || s === "in_review";
  });
  const displayLeaves =
    pendingLeaves.length > 0
      ? pendingLeaves
      : Array.isArray(leaves)
      ? leaves
      : [];

  // Filter requisitions
  const safeRequisitions = Array.isArray(requisitions) ? requisitions : [];
  const pendingRequisitions = safeRequisitions.filter((r) => {
    if (!r) return false;
    const s = String(
      r.status || r.approval_status || r.requisition_status || "",
    ).toLowerCase();
    return (
      !s ||
      s === "pending" ||
      s === "submitted" ||
      s === "in_review" ||
      s === "open"
    );
  });
  const displayRequisitions =
    pendingRequisitions.length > 0 ? pendingRequisitions : safeRequisitions;

  if (!showLeaves && !showRequisitions) {
    return null;
  }

  const formatLeaveDate = (val) => {
    if (!val) return "";
    const date = new Date(val);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getDayCountText = (from, to) => {
    if (!from || !to) return "";
    const start = new Date(from);
    const end = new Date(to);
    const diff = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
    return `(${diff} ${diff === 1 ? "Day" : "Days"})`;
  };

  const getSubmittedAgoText = (dateStr) => {
    if (!dateStr) return "Submitted recently";
    const date = new Date(dateStr);
    const diffMin = Math.floor((Date.now() - date.getTime()) / (1000 * 60));
    if (diffMin < 60) return `Submitted ${Math.max(1, diffMin)}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `Submitted ${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Submitted Yesterday";
    return `Submitted ${diffDays}d ago`;
  };

  const handleLeaveAction = async (leaveId, action) => {
    try {
      setActionLoadingId(`${leaveId}-${action}`);
      await dispatch(updateLeaveAction(leaveId, { action }));
    } catch (err) {
      console.error("Failed to update leave:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "4px",
              bgcolor: isDark ? alpha("#4f46e5", 0.2) : "#eef2ff",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdPendingActions size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
            }}
          >
            Pending Approvals
          </Typography>
        </Stack>

        {/* Tab Switchers styled as sharp segmented pills */}
        {showLeaves && showRequisitions && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              p: 0.5,
              bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
              borderRadius: "6px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <Button
              size="small"
              onClick={() => setActiveTab(0)}
              sx={{
                py: 0.4,
                px: 1.25,
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.74rem",
                fontWeight: activeTab === 0 ? 700 : 500,
                color: activeTab === 0 ? "#4f46e5" : "text.secondary",
                bgcolor:
                  activeTab === 0
                    ? isDark
                      ? alpha("#4f46e5", 0.2)
                      : "#ffffff"
                    : "transparent",
                boxShadow:
                  activeTab === 0 && !isDark
                    ? "0 1px 3px rgba(0,0,0,0.08)"
                    : "none",
              }}
            >
              Leave Requests ({displayLeaves.length})
            </Button>
            <Button
              size="small"
              onClick={() => setActiveTab(1)}
              sx={{
                py: 0.4,
                px: 1.25,
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "0.74rem",
                fontWeight: activeTab === 1 ? 700 : 500,
                color: activeTab === 1 ? "#4f46e5" : "text.secondary",
                bgcolor:
                  activeTab === 1
                    ? isDark
                      ? alpha("#4f46e5", 0.2)
                      : "#ffffff"
                    : "transparent",
                boxShadow:
                  activeTab === 1 && !isDark
                    ? "0 1px 3px rgba(0,0,0,0.08)"
                    : "none",
              }}
            >
              Requisitions ({displayRequisitions.length})
            </Button>
          </Stack>
        )}
      </Box>

      {/* List of Approval Cards */}
      {activeTab === 0 ? (
        displayLeaves.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No pending leave requests.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {displayLeaves.slice(0, 3).map((leave, idx) => {
              const empName =
                leave.employee_name ||
                leave.applicant ||
                (leave.first_name
                  ? `${leave.first_name} ${leave.last_name || ""}`.trim()
                  : null) ||
                currentUser?.first_name ||
                currentUser?.name ||
                "Employee";
              const leaveType =
                leave.leave_type ||
                leave.leave_type_name ||
                leave.type_name ||
                "Sick Leave";
              const reason = leave.reason || leave.remarks || leave.note || "";
              const dateRange = `${formatLeaveDate(leave.from_date || leave.start_date)} - ${formatLeaveDate(leave.to_date || leave.end_date)} ${getDayCountText(leave.from_date || leave.start_date, leave.to_date || leave.end_date)}`;
              const submittedText = getSubmittedAgoText(leave.created_at || leave.apply_date);

              const isApproving = actionLoadingId === `${leave.id}-approve`;
              const isRejecting = actionLoadingId === `${leave.id}-reject`;

              return (
                <Box
                  key={leave.id || idx}
                  sx={{
                    p: 1.75,
                    borderRadius: "6px",
                    bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: isDark ? "rgba(255,255,255,0.18)" : "#cbd5e1",
                    },
                  }}
                >
                  {/* Left: Avatar & Info */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 240 }}>
                    <Avatar
                      src={leave.profile_pic_url || leave.avatar}
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        bgcolor: isDark ? "#334155" : "#e2e8f0",
                        color: isDark ? "#f8fafc" : "#1e293b",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#cbd5e1"}`,
                      }}
                    >
                      {(empName || "E")[0]?.toUpperCase()}
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.88rem" }}
                        >
                          {empName}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            px: 0.85,
                            py: 0.15,
                            borderRadius: "4px",
                            bgcolor: isDark ? alpha("#f43f5e", 0.15) : "#ffe4e6",
                            color: "#e11d48",
                          }}
                        >
                          {leaveType}
                        </Box>
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontSize: "0.72rem",
                          mb: 0.5,
                        }}
                      >
                        {submittedText}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.78rem",
                          color: "text.primary",
                          lineHeight: 1.35,
                        }}
                      >
                        <strong>{dateRange}</strong>
                        {reason && ` • "${reason}"`}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Right: Actions */}
                  {canApproveLeave ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={Boolean(actionLoadingId)}
                        onClick={() => handleLeaveAction(leave.id, "approve")}
                        startIcon={
                          isApproving ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <MdCheck size={16} />
                          )
                        }
                        sx={{
                          bgcolor: "#065f46",
                          color: "#ffffff",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "none",
                          borderRadius: "4px",
                          px: 1.5,
                          py: 0.5,
                          "&:hover": { bgcolor: "#047857" },
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={Boolean(actionLoadingId)}
                        onClick={() => handleLeaveAction(leave.id, "reject")}
                        startIcon={
                          isRejecting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <MdClose size={16} />
                          )
                        }
                        sx={{
                          borderColor: isDark ? alpha("#ef4444", 0.5) : "#fca5a5",
                          color: "#dc2626",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "none",
                          borderRadius: "4px",
                          px: 1.25,
                          py: 0.5,
                          "&:hover": {
                            bgcolor: isDark ? alpha("#ef4444", 0.1) : "#fee2e2",
                            borderColor: "#dc2626",
                          },
                        }}
                      >
                        Reject
                      </Button>
                    </Stack>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: "4px",
                        bgcolor: isDark ? alpha("#f59e0b", 0.15) : "#fef3c7",
                        color: "#b45309",
                        border: `1px solid ${isDark ? alpha("#f59e0b", 0.3) : "#fde68a"}`,
                      }}
                    >
                      {leave.status || "Pending"}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )
      ) : displayRequisitions.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No pending requisitions.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {displayRequisitions.slice(0, 3).map((req, idx) => {
            const reqTitle =
              req.requisition_no ||
              req.requisition_type ||
              req.title ||
              req.item_name ||
              req.project_name ||
              `Requisition #${req.id || idx + 1}`;
            const requestedBy =
              req.requested_by_name ||
              req.employee_name ||
              req.requested_by ||
              "Staff";
            const reqDate = req.created_at || req.date;

            return (
              <Box
                key={req.id || idx}
                sx={{
                  p: 1.75,
                  borderRadius: "6px",
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 240 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      bgcolor: isDark ? alpha("#2563eb", 0.2) : "#eff6ff",
                      color: "#2563eb",
                      border: `1px solid ${isDark ? alpha("#2563eb", 0.4) : "#bfdbfe"}`,
                    }}
                  >
                    {(reqTitle || "R")[0]?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.88rem" }}
                    >
                      {reqTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requested by: <strong>{requestedBy}</strong> • {getSubmittedAgoText(reqDate)}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate("/company/requisitions")}
                  sx={{
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "#4f46e5",
                    borderColor: isDark ? alpha("#4f46e5", 0.4) : "#c7d2fe",
                    "&:hover": {
                      borderColor: "#4f46e5",
                      bgcolor: isDark ? alpha("#4f46e5", 0.1) : "#eef2ff",
                    },
                  }}
                >
                  Review
                </Button>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Footer link to manage all */}
      <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}` }}>
        <Typography
          variant="caption"
          onClick={() => {
            if (activeTab === 0) {
              navigate(canApproveLeave ? "/company/leave-details" : "/company/apply-leave");
            } else {
              navigate("/company/requisitions");
            }
          }}
          sx={{
            fontWeight: 600,
            color: "#4f46e5",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View all {activeTab === 0 ? "leave requests" : "requisitions"} &gt;
        </Typography>
      </Box>
    </Paper>
  );
};

export default PendingApprovalsWidget;
